import React, { useState, useEffect } from 'react';
import { api } from '../api';
import ReviewChangesModal from './ReviewChangesModal';

const SubscriptionManagementFinal = ({ user }) => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [processing, setProcessing] = useState(false);
  const [addonPrices, setAddonPrices] = useState({});
  const [quantities, setQuantities] = useState({
    extra_homes: 1,
    extra_storage: 1
  });
  const [loadingPortal, setLoadingPortal] = useState(false);
  
  // Review Modal State
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({
    planChange: null,
    addonsToAdd: [],
    addonsToRemove: []
  });
  
  // Multi-addon selection mode
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedAddons, setSelectedAddons] = useState([]);
  
  // Cancellation flow
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelStep, setCancelStep] = useState(1);
  const [cancelConfirmations, setCancelConfirmations] = useState({
    immediateAccess: false,
    dataExport: false
  });

  // ACTUAL Stripe Plans from hoa_signup_payment.py
  const STRIPE_PLANS = {
    basic: {
      name: "Platform Base",
      yearly_price_id: "price_1SMByyFuWSwki2tlunKmG3e3",
      monthly_price_id: "price_1SMByyFuWSwki2tlXOj2H9Xp",
      max_homes: 100,
      storage_gb: 50
    },
    standard: {
      name: "Platform Standard",
      yearly_price_id: "price_1SMCANFuWSwki2tljNvnIar0",
      monthly_price_id: "price_1SMCANFuWSwki2tle6Aybwu3",
      max_homes: 300,
      storage_gb: 25  // Corrected from 200 to 25GB
    },
    premium: {
      name: "Platform Pro",
      yearly_price_id: "price_1SMCCBFuWSwki2tlIm6QbXYg",
      monthly_price_id: "price_1SMCCBFuWSwki2tl247tCsHS",
      max_homes: 1000,
      storage_gb: 1000
    }
  };

  // ACTUAL Stripe Add-ons from hoa_signup_payment.py
  const STRIPE_ADDONS = {
    budget_finance: {
      name: "Budget & Finance Reporting",
      yearly_price_id: "price_1SMGoUFuWSwki2tlxNU0ooW4",
      monthly_price_id: "price_1SMGoUFuWSwki2tlCRZ07YvQ",
      included_in: ["standard", "premium"]
    },
    amenity_management: {
      name: "Amenity Management",
      yearly_price_id: "price_1SMGl7FuWSwki2tllFtVv769",
      monthly_price_id: "price_1SMGl7FuWSwki2tlyqPc3hGO"
    },
    voting_platform: {
      name: "Voting Platform",
      yearly_price_id: "price_1SMCDMFuWSwki2tlCrkHg3O4",
      monthly_price_id: "price_1SMCDMFuWSwki2tl3UQNumvm"
    },
    custom_pages: {
      name: "Custom Pages",
      yearly_price_id: "price_1SMC9TFuWSwki2tlRjooENO7",
      monthly_price_id: "price_1SMC9TFuWSwki2tlPoGxqADM",
      included_in: ["premium"]
    },
    extra_homes: {
      name: "Extra Homes",
      yearly_price_id: "price_1SMC4xFuWSwki2tlOme3A2MV",
      monthly_price_id: "price_1SMC5YFuWSwki2tlIj1raJk2",
      quantity_based: true,
      per_unit: true
    },
    additional_storage: {
      name: "Additional Storage",
      yearly_price_id: "price_1SMC6VFuWSwki2tlaoobbnZB",
      monthly_price_id: "price_1SMC6VFuWSwki2tlRjooENO7",
      quantity_based: true,
      per_unit: true
    },
    guest_management: {
      name: "Guest Management",
      yearly_price_id: "price_1SMC3XFuWSwki2tlPNmuBNq1",
      monthly_price_id: "price_1SMC44FuWSwki2tlh7wspmYu"
    }
  };

  useEffect(() => {
    loadSubscription();
    loadPrices();
  }, [user?.hoa_id]);

  const loadSubscription = async () => {
    setLoading(true);
    setMessage(''); // Clear message at start
    
    try {
      console.log('🔍 Loading subscription for HOA:', user.hoa_id);
      const response = await api.get(`/api/hoa/${user.hoa_id}/settings`);
      console.log('📦 Full response received:', JSON.stringify(response.data, null, 2));
      
      if (response.data?.success && response.data?.settings) {
        const sub = response.data.settings.subscription;
        
        if (sub) {
          console.log('✅ Subscription loaded via centralized service:', {
            plan_name: sub.plan?.name,
            plan_tier: sub.plan?.tier,
            add_ons_count: sub.add_ons?.length || 0,
            add_ons: sub.add_ons?.map(a => a.id || a.name) || []
          });
          
          setSubscription(sub);
          setMessage(''); // Clear any previous error messages
          
          // Load current quantities for quantity-based add-ons from add_ons array
          if (sub.add_ons && Array.isArray(sub.add_ons)) {
            const extraHomes = sub.add_ons.find(a => a.id === 'extra_homes' || a.addon_id === 'extra_homes');
            const extraStorage = sub.add_ons.find(a => a.id === 'extra_storage' || a.addon_id === 'extra_storage');
            
            setQuantities({
              extra_homes: extraHomes?.quantity || 1,
              extra_storage: extraStorage?.quantity || 1
            });
          }
        } else {
          console.warn('⚠️ No subscription data in response');
          setMessage('');
        }
      } else {
        console.error('❌ Unexpected response format:', response.data);
        setMessage('');
      }
    } catch (error) {
      console.error('❌ Error loading subscription:', error);
      console.error('Error details:', error.response?.data);
      // Don't show error message to avoid confusing user
      setMessage('');
    }
    setLoading(false);
  };

  const loadPrices = async () => {
    try {
      const response = await api.get('/api/subscription/prices');
      console.log('🔍 PRICING API RESPONSE:', response.data);
      if (response.data?.success) {
        console.log('💰 ADDON PRICES LOADED:', response.data.prices);
        console.log('💰 Additional Storage:', response.data.prices?.additional_storage);
        console.log('💰 Extra Homes:', response.data.prices?.extra_homes);
        setAddonPrices(response.data.prices);
      }
    } catch (error) {
      console.error('Error loading prices:', error);
    }
  };

  const hasAddon = (addonId) => {
    if (!subscription) {
      console.log('❌ No subscription data available');
      return false;
    }
    
    console.log('🔍 Checking addon:', addonId, 'in subscription:', {
      has_add_ons: !!subscription.add_ons,
      add_ons_count: subscription.add_ons?.length,
      addon_ids: subscription.add_ons?.map(a => a.id)
    });
    
    // PRIMARY: Check add_ons array (from hoa_subscriptions collection via centralized service)
    if (subscription.add_ons && Array.isArray(subscription.add_ons)) {
      const found = subscription.add_ons.some(addon => 
        addon.id === addonId || addon.addon_id === addonId
      );
      if (found) {
        console.log('✅ Addon found in add_ons array');
        return true;
      }
    }
    
    // LEGACY: Check simple array format (backward compatibility)
    if (Array.isArray(subscription.addons) && subscription.addons.includes(addonId)) {
      console.log('✅ Addon found in legacy addons array');
      return true;
    }
    
    // LEGACY: Check Stripe format (backward compatibility)
    if (subscription.plan?.add_ons && Array.isArray(subscription.plan.add_ons)) {
      const found = subscription.plan.add_ons.some(addon => 
        addon.addon_id === addonId || addon.id === addonId
      );
      if (found) {
        console.log('✅ Addon found in plan.add_ons');
        return true;
      }
    }
    
    console.log('❌ Addon not found in any format');
    return false;
  };

  const currentTier = subscription?.plan?.tier || 'basic';

  // Toggle addon selection
  const toggleAddonSelection = (addonId) => {
    setSelectedAddons(prev => {
      if (prev.includes(addonId)) {
        return prev.filter(id => id !== addonId);
      } else {
        return [...prev, addonId];
      }
    });
  };

  // Start multi-addon selection mode
  const startSelectionMode = () => {
    setSelectionMode(true);
    setSelectedAddons([]);
  };

  // Review selected addons
  const reviewSelectedAddons = () => {
    if (selectedAddons.length === 0) {
      alert('Please select at least one add-on to continue.');
      return;
    }

    const addonsToAdd = selectedAddons.map(addonId => {
      const addon = STRIPE_ADDONS[addonId];
      return {
        id: addonId,
        name: addon.name,
        quantity: quantities[addonId] || 1  // USE ACTUAL QUANTITY FROM STATE
      };
    });

    setPendingChanges({
      planChange: null,
      addonsToAdd: addonsToAdd,
      addonsToRemove: []
    });
    
    setShowReviewModal(true);
    setSelectionMode(false);
  };

  // Add single addon
  const addAddonToPending = (addonId, quantity = 1) => {
    const addon = STRIPE_ADDONS[addonId];
    if (!addon) return;
    
    setPendingChanges({
      planChange: null,
      addonsToAdd: [{
        id: addonId,
        name: addon.name,
        quantity: quantity
      }],
      addonsToRemove: []
    });
    
    setShowReviewModal(true);
  };
  
  // Remove addon
  const removeAddonPending = (addonId) => {
    const addon = STRIPE_ADDONS[addonId];
    if (!addon) return;
    
    setPendingChanges({
      planChange: null,
      addonsToAdd: [],
      addonsToRemove: [{
        id: addonId,
        name: addon.name
      }]
    });
    
    setShowReviewModal(true);
  };
  
  // Confirm changes and create checkout session
  const confirmSubscriptionChanges = async () => {
    setProcessing(true);
    setMessage('');
    
    try {
      // Call the V2 API to create checkout session
      const response = await api.post('/api/subscription/create-checkout', {
        hoa_id: user.hoa_id,
        changes: {
          plan_change: pendingChanges.planChange,
          addons_to_add: pendingChanges.addonsToAdd,
          addons_to_remove: pendingChanges.addonsToRemove
        }
      });
      
      if (response.data?.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
      } else {
        setMessage('✅ Changes processed successfully!');
        await loadSubscription();
        setShowReviewModal(false);
        setPendingChanges({ planChange: null, addonsToAdd: [], addonsToRemove: [] });
      }
    } catch (error) {
      console.error('Error confirming changes:', error);
      setMessage(`❌ ${error.response?.data?.detail || 'Failed to process changes'}`);
    }
    setProcessing(false);
  };
  
  // Cancel subscription with confirmations
  const handleCancelSubscription = async () => {
    if (!cancelConfirmations.immediateAccess || !cancelConfirmations.dataExport) {
      alert('Please confirm both requirements to proceed with cancellation.');
      return;
    }

    setProcessing(true);
    try {
      const response = await api.post('/api/subscription/cancel', {
        hoa_id: user.hoa_id,
        confirm_immediate_lockout: cancelConfirmations.immediateAccess,
        confirm_data_export: cancelConfirmations.dataExport
      });

      if (response.data.success) {
        alert('Subscription cancelled. All users will be locked out immediately. You can restart your subscription at any time from the login page.');
        // Logout user
        window.location.href = '/';
      }
    } catch (error) {
      setMessage(`❌ ${error.response?.data?.detail || 'Failed to cancel subscription'}`);
    }
    setProcessing(false);
  };

  // Legacy function for quantity updates (immediate)
  const handleQuantityUpdate = async (addonId, quantity) => {
    setProcessing(true);
    setMessage('');
    
    try {
      const response = await api.post('/api/subscription/modify-subscription', {
        hoa_id: user.hoa_id,
        addon_id: addonId,
        action: 'update_quantity',
        payment_frequency: 'yearly',
        quantity: quantity
      });
      
      setMessage(`✅ ${response.data.message || 'Quantity updated!'}`);
      await loadSubscription();
    } catch (error) {
      setMessage(`❌ ${error.response?.data?.detail || 'Failed to update quantity'}`);
    }
    setProcessing(false);
  };

  if (loading) {
    return <div className="text-center py-8">Loading subscription information...</div>;
  }

  const currentPlan = STRIPE_PLANS[currentTier];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Subscription Management</h2>
        <p className="text-gray-600">Manage your plan and add-ons</p>
      </div>

      {message && (
        <div className={`p-4 rounded-lg ${message.startsWith('✅') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
          {message}
        </div>
      )}

      {/* Current Plan */}
      <div className="bg-white border-2 border-indigo-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Plan</h3>
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="text-2xl font-bold text-indigo-600">{currentPlan?.name || 'Platform Base'}</div>
            <div className="text-sm text-gray-600 mt-1">
              {currentPlan?.max_homes || 100} homes • {currentPlan?.storage_gb || 50}GB storage
            </div>
            
            {/* Billing Information */}
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div className="bg-indigo-50 p-3 rounded">
                <div className="text-xs text-indigo-600 font-medium">Next Billing Date</div>
                <div className="text-sm font-semibold text-indigo-900 mt-1">
                  {subscription?.next_billing_date 
                    ? new Date(subscription.next_billing_date).toLocaleDateString('en-US', { 
                        month: 'long', 
                        day: 'numeric', 
                        year: 'numeric' 
                      })
                    : subscription?.renewal_date 
                    ? new Date(subscription.renewal_date * 1000).toLocaleDateString('en-US', { 
                        month: 'long', 
                        day: 'numeric', 
                        year: 'numeric' 
                      })
                    : 'Not available'}
                </div>
              </div>
              <div className="bg-indigo-50 p-3 rounded">
                <div className="text-xs text-indigo-600 font-medium">Billing Frequency</div>
                <div className="text-sm font-semibold text-indigo-900 mt-1">
                  {subscription?.plan?.interval === 'month' ? 'Monthly' : 'Yearly'}
                </div>
              </div>
            </div>
            
            {/* Stripe Customer Portal Button */}
            <div className="mt-4 pt-4 border-t border-indigo-100">
              <button
                onClick={async () => {
                  setLoadingPortal(true);
                  try {
                    const { data } = await api.post('/api/subscription/portal');
                    if (data.url) {
                      window.open(data.url, '_blank');
                    } else {
                      alert('Failed to open billing portal');
                    }
                  } catch (error) {
                    console.error('Error opening portal:', error);
                    alert('Failed to open billing portal. Please try again.');
                  } finally {
                    setLoadingPortal(false);
                  }
                }}
                disabled={loadingPortal}
                className="w-full px-4 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loadingPortal ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Opening...
                  </>
                ) : (
                  <>
                    💳 Manage Billing & Payment Methods
                  </>
                )}
              </button>
              <p className="text-xs text-gray-500 mt-2 text-center">
                Update payment methods, view invoices, and manage your subscription
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Add-ons */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Available Add-ons</h3>
          {!selectionMode ? (
            <button
              onClick={startSelectionMode}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md"
            >
              Select Multiple Add-ons
            </button>
          ) : (
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  setSelectionMode(false);
                  setSelectedAddons([]);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={reviewSelectedAddons}
                disabled={selectedAddons.length === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Review {selectedAddons.length} Add-on{selectedAddons.length !== 1 ? 's' : ''}
              </button>
            </div>
          )}
        </div>
        <div className="space-y-4">
          {Object.entries(STRIPE_ADDONS).map(([addonId, addon]) => {
            const isIncluded = addon.included_in?.includes(currentTier);
            const hasIt = hasAddon(addonId);
            const isGreyedOut = isIncluded;

            const isSelected = selectedAddons.includes(addonId);
            
            return (
              <div 
                key={addonId}
                className={`border rounded-lg p-4 ${
                  isGreyedOut ? 'bg-gray-50 border-gray-300' : 
                  hasIt ? 'bg-green-50 border-green-300' : 
                  isSelected ? 'bg-indigo-50 border-indigo-500 border-2' :
                  'border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between">
                  {selectionMode && !hasIt && !isIncluded && (
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleAddonSelection(addonId)}
                      className="mt-1 mr-3 h-5 w-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                  )}
                  <div className="flex-1">
                    <h4 className={`font-medium ${isGreyedOut ? 'text-gray-500' : 'text-gray-900'}`}>
                      {addon.name}
                      {isIncluded && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                          Included in {currentTier.charAt(0).toUpperCase() + currentTier.slice(1)}
                        </span>
                      )}
                      {hasIt && !isIncluded && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                          Active
                        </span>
                      )}
                    </h4>
                    
                    {/* Pricing */}
                    <div className={`text-sm mt-2 ${isGreyedOut ? 'text-gray-500' : 'text-indigo-600'} font-medium`}>
                      {addonPrices[addonId] ? (
                        <>
                          ${addonPrices[addonId].yearly}/year or ${addonPrices[addonId].monthly}/month
                          {addon.per_unit && <span className="text-xs text-gray-600"> per unit</span>}
                        </>
                      ) : (
                        'Pricing available at checkout'
                      )}
                    </div>
                    
                    {/* Quantity Controls for quantity-based add-ons - Show ALWAYS if quantity_based */}
                    {addon.quantity_based && !isIncluded && (
                      <div className="mt-3 flex items-center space-x-2">
                        <label className="text-sm font-medium text-gray-700">Quantity:</label>
                        <button
                          onClick={() => {
                            const newQty = Math.max(1, (quantities[addonId] || 1) - 1);
                            setQuantities({...quantities, [addonId]: newQty});
                            if (hasIt) handleQuantityUpdate(addonId, newQty);
                          }}
                          disabled={processing || (quantities[addonId] || 1) <= 1}
                          className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                        >
                          -
                        </button>
                        <input
                          type="number"
                          min="1"
                          value={quantities[addonId] || 1}
                          onChange={(e) => {
                            const newQty = Math.max(1, parseInt(e.target.value) || 1);
                            setQuantities({...quantities, [addonId]: newQty});
                          }}
                          onBlur={() => {
                            if (hasIt) handleQuantityUpdate(addonId, quantities[addonId] || 1);
                          }}
                          className="w-16 px-2 py-1 text-sm text-center font-medium border border-gray-300 rounded"
                        />
                        <button
                          onClick={() => {
                            const newQty = (quantities[addonId] || 1) + 1;
                            setQuantities({...quantities, [addonId]: newQty});
                            if (hasIt) handleQuantityUpdate(addonId, newQty);
                          }}
                          disabled={processing}
                          className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                        >
                          +
                        </button>
                        {!hasIt && (
                          <span className="text-xs text-gray-500 ml-2">
                            Total: ${((addonPrices[addonId]?.yearly || 0) * (quantities[addonId] || 1)).toFixed(2)}/year
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {!selectionMode && (
                    <div className="ml-4">
                      {!isIncluded && (
                        hasIt ? (
                          <button
                            onClick={() => removeAddonPending(addonId)}
                            disabled={processing}
                            className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 border border-red-300 rounded-md hover:bg-red-50 disabled:opacity-50"
                          >
                            Remove
                          </button>
                        ) : (
                          <button
                            onClick={() => addAddonToPending(addonId, quantities[addonId] || 1)}
                            disabled={processing}
                            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md disabled:opacity-50"
                          >
                            {addon.quantity_based ? `Add ${quantities[addonId] || 1}` : 'Add to Subscription'}
                          </button>
                        )
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Cancel Subscription Section */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-red-900 mb-2">Danger Zone</h3>
        <p className="text-sm text-red-700 mb-4">
          Canceling your subscription will immediately lock out all users from the HOA platform. Your data will be preserved and you can restart your subscription at any time.
        </p>
        <button
          onClick={() => {
            setShowCancelModal(true);
            setCancelStep(1);
            setCancelConfirmations({ immediateAccess: false, dataExport: false });
          }}
          className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
        >
          Cancel Subscription
        </button>
      </div>

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            {/* Step 1: Warning */}
            {cancelStep === 1 && (
              <>
                <div className="p-6 border-b border-gray-200">
                  <h2 className="text-xl font-bold text-red-900">⚠️ Cancel Subscription</h2>
                </div>
                <div className="p-6">
                  <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
                    <p className="text-red-800 font-semibold">
                      Are you sure you want to cancel your subscription?
                    </p>
                    <p className="text-red-700 text-sm mt-2">
                      All users for your HOA will lose access immediately. No one will be able to login until you restart your subscription.
                    </p>
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => setShowCancelModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Go Back
                    </button>
                    <button
                      onClick={() => setCancelStep(2)}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                    >
                      Continue
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Step 2: Data Export Warning */}
            {cancelStep === 2 && (
              <>
                <div className="p-6 border-b border-gray-200">
                  <h2 className="text-xl font-bold text-orange-900">📥 Export Your Data</h2>
                </div>
                <div className="p-6">
                  <div className="bg-orange-50 border-l-4 border-orange-500 p-4 mb-4">
                    <p className="text-orange-800 font-semibold">
                      Before canceling, make sure you export any data you need:
                    </p>
                    <ul className="text-orange-700 text-sm mt-2 list-disc list-inside space-y-1">
                      <li>Download all important documents and files</li>
                      <li>Export CSV reports (financial, members, voting records)</li>
                      <li>Save any meeting minutes or agendas</li>
                      <li>Download community photos and resources</li>
                    </ul>
                  </div>
                  <div className="mb-4">
                    <label className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        checked={cancelConfirmations.dataExport}
                        onChange={(e) => setCancelConfirmations({...cancelConfirmations, dataExport: e.target.checked})}
                        className="mt-1 h-5 w-5 text-red-600 border-gray-300 rounded focus:ring-red-500"
                      />
                      <span className="text-sm text-gray-700">
                        I confirm that I have exported all necessary CSV files and documents I may need.
                      </span>
                    </label>
                  </div>
                  <div className="mb-4">
                    <label className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        checked={cancelConfirmations.immediateAccess}
                        onChange={(e) => setCancelConfirmations({...cancelConfirmations, immediateAccess: e.target.checked})}
                        className="mt-1 h-5 w-5 text-red-600 border-gray-300 rounded focus:ring-red-500"
                      />
                      <span className="text-sm text-gray-700">
                        I understand that all users will lose access immediately and can only regain access by restarting the subscription through Stripe.
                      </span>
                    </label>
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => setCancelStep(1)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleCancelSubscription}
                      disabled={!cancelConfirmations.dataExport || !cancelConfirmations.immediateAccess || processing}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {processing ? 'Canceling...' : 'Cancel My Subscription'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Review Changes Modal */}
      <ReviewChangesModal
        isOpen={showReviewModal}
        onClose={() => {
          setShowReviewModal(false);
          setPendingChanges({ planChange: null, addonsToAdd: [], addonsToRemove: [] });
        }}
        onConfirm={confirmSubscriptionChanges}
        currentPlan={{
          name: currentPlan?.name || 'Platform Base',
          addons: subscription?.add_ons || subscription?.addons || []
        }}
        newPlan={{
          name: currentPlan?.name || 'Platform Base'
        }}
        changes={pendingChanges}
        pricing={{
          currentTotal: subscription?.total_amount || 0,
          newPlanPrice: subscription?.plan?.price_per_year || 0,
          addonPrices: Object.keys(addonPrices).reduce((acc, key) => {
            acc[key] = subscription?.payment_frequency === 'monthly' 
              ? addonPrices[key]?.monthly || 0
              : addonPrices[key]?.yearly || 0;
            return acc;
          }, {})
        }}
        billingInfo={{
          frequency: subscription?.payment_frequency === 'monthly' ? 'Monthly' : 'Yearly',
          nextBillingDate: subscription?.next_billing_date 
            ? new Date(subscription.next_billing_date).toLocaleDateString('en-US', { 
                month: 'long', 
                day: 'numeric', 
                year: 'numeric' 
              })
            : null,
          currentPlanPrice: subscription?.plan?.price_per_year || 0
        }}
        loading={processing}
      />
    </div>
  );
};

export default SubscriptionManagementFinal;
